import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Loader2, Mail } from 'lucide-react';
import { authAPI } from '../services/api';
import { useBranding } from '../hooks/useBranding';

const registerSchema = z.object({
  email: z.string().email('Please enter a valid email'),
});

export default function RegisterPage() {
  const branding = useBranding();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await authAPI.register({ email: data.email });
      setSubmittedEmail(data.email);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await authAPI.resendVerification(submittedEmail);
    } catch (err) {
      // Don't show error - endpoint always returns success for security
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state - show check email message
  if (success) {
    return (
      <div className="animate-fade-in text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="w-8 h-8 text-green-600" />
        </div>

        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Check your email</h2>
        <p className="text-neutral-600 mb-2">
          We sent a verification link to:
        </p>
        <p className="font-medium text-neutral-900 mb-6">
          {submittedEmail}
        </p>

        <p className="text-sm text-neutral-500 mb-8">
          Click the link in the email to complete your registration and set your password.
          The link expires in 24 hours.
        </p>

        <div className="space-y-4">
          <button
            onClick={handleResendEmail}
            disabled={isSubmitting}
            className="w-full btn-secondary py-3"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : (
              'Resend verification email'
            )}
          </button>

          <p className="text-sm text-neutral-500">
            Wrong email?{' '}
            <button
              onClick={() => {
                setSuccess(false);
                setSubmittedEmail('');
              }}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Try again
            </button>
          </p>
        </div>

        <p className="mt-8 text-sm text-neutral-500">
          Already verified?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-neutral-900">Create your account</h2>
        <p className="text-neutral-600 mt-2">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            Sign in
          </Link>
        </p>
      </div>

      {/* Info box */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-blue-800 text-sm">
          <strong>Note:</strong> Portal accounts are linked to your company record.
          Your email must match an existing contact in our system.
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label htmlFor="email" className="label">Work email</label>
          <input
            {...register('email')}
            type="email"
            id="email"
            autoComplete="email"
            className={`input ${errors.email ? 'input-error' : ''}`}
            placeholder="you@company.com"
          />
          {errors.email && (
            <p className="error-text">{errors.email.message}</p>
          )}
        </div>

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="terms"
            required
            className="mt-1 w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
          />
          <label htmlFor="terms" className="text-sm text-neutral-600">
            I agree to the{' '}
            <a href="#" className="text-primary-600 hover:text-primary-700">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-primary-600 hover:text-primary-700">Privacy Policy</a>
          </label>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full btn-primary py-3"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Sending verification email...
            </>
          ) : (
            'Continue with email'
          )}
        </button>
      </form>

      {branding.supportEmail && (
        <p className="mt-8 text-center text-sm text-neutral-500">
          Need help? Contact us at{' '}
          <a href={`mailto:${branding.supportEmail}`} className="text-primary-600 hover:text-primary-700">
            {branding.supportEmail}
          </a>
        </p>
      )}
    </div>
  );
}
